/**
 * Règles pures de l'écran Fluides, testées unitairement : ce que l'écran
 * envoie au serveur et ce qu'il lit dans l'adresse.
 */
import {
  CERFA_NATURES, FLUID_NATURES, FLUID_REASON_MIN, cylinderDeltaGrams, formatKg, parseKg,
} from '@myequip/shared';

// --- Registre : inscrire une opération ----------------------------------------------------

export type MachineOperation = 'charge' | 'recuperation';

/** Ce qu'il faut savoir de la bouteille pour contrôler la saisie à l'écran (le serveur revérifie). */
export interface CylinderForOperation {
  kind: 'charge' | 'recuperation';
  current_grams: number;
  capacity_grams: number;
}

export interface OperationDraft {
  operation: MachineOperation | '';
  /** Quantité saisie en kilogrammes (« 0,5 »). */
  kg: string;
  fromRecoveryConfirmed: boolean;
  /** Attestation d'aptitude de l'opérateur choisi ; `null` : pas encore choisi. */
  operatorAptitude: boolean | null;
  when: 'maintenant' | 'jour';
  day: string;
}

/**
 * Contenu de bouteille que la saisie contredit : charge plus grande que le
 * contenu connu, ou récupération qui déborderait. Clé de
 * `fluides.nouvelleOperation` et ses valeurs, ou `null`.
 */
export function cylinderContentProblem(
  operation: MachineOperation | '',
  grams: number,
  cylinder: CylinderForOperation,
): { key: string; values: Record<string, string> } | null {
  if (operation === 'charge' && grams > cylinder.current_grams) {
    return { key: 'bouteilleInsuffisante', values: { contenu: formatKg(cylinder.current_grams / 1000) } };
  }
  if (operation === 'recuperation' && cylinder.current_grams + grams > cylinder.capacity_grams) {
    return { key: 'bouteillePleine', values: { capacite: formatKg(cylinder.capacity_grams / 1000) } };
  }
  return null;
}

/**
 * Ce qui manque ou cloche dans la saisie d'une opération, dans l'ordre des
 * étapes : clé de `fluides.nouvelleOperation` et ses valeurs, ou `null`.
 * En ligne, un contenu contredit bloque, comme le serveur (409). Sans réseau
 * (`offline`), il ne bloque pas : la fiche de la bouteille gardée sur
 * l'appareil peut être fausse, le geste fait sur place est inscrit au retour
 * du réseau et marqué « À vérifier » ; l'écran l'annonce par un avertissement.
 */
export function operationProblem(
  draft: OperationDraft,
  cylinder: CylinderForOperation | null,
  machineChosen: boolean,
  options: { offline?: boolean } = {},
): { key: string; values?: Record<string, string> } | null {
  if (!cylinder) return { key: 'manqueBouteille' };
  if (!draft.operation) return { key: 'manqueOperation' };
  if (!machineChosen) return { key: 'manqueMachine' };
  const grams = gramsFromKg(draft.kg);
  if (!grams || grams <= 0) return { key: 'manqueQuantite' };
  const content = options.offline ? null : cylinderContentProblem(draft.operation, grams, cylinder);
  if (content) return content;
  if (draft.operation === 'charge' && cylinder.kind === 'recuperation' && !draft.fromRecoveryConfirmed) {
    return { key: 'manqueRecyclage' };
  }
  if (draft.operatorAptitude !== true) return { key: 'manqueOperateur' };
  if (draft.when === 'jour' && !/^\d{4}-\d{2}-\d{2}$/u.test(draft.day)) return { key: 'manqueJour' };
  return null;
}

/** Contenu de la bouteille après l'opération, en grammes, entre zéro et sa capacité (comme le serveur). */
export function contentAfter(cylinder: CylinderForOperation, operation: MachineOperation, grams: number): number {
  const after = operation === 'charge' ? cylinder.current_grams - grams : cylinder.current_grams + grams;
  return Math.min(cylinder.capacity_grams, Math.max(0, after));
}

/**
 * Heure du geste envoyée au serveur : rien pour « maintenant » (l'heure du
 * serveur fait foi), midi à l'heure de l'appareil pour un autre jour. Choisi
 * le jour même avant midi, ce serait l'avenir, que le serveur refuse : l'heure
 * de la saisie sert alors. Un jour futur ou trop ancien est refusé par le serveur.
 */
export function performedAtOf(when: 'maintenant' | 'jour', day: string, now: Date = new Date()): string | undefined {
  if (when === 'maintenant') return undefined;
  const [year, month, date] = day.split('-').map(Number);
  const noon = new Date(year!, month! - 1, date!, 12, 0, 0);
  return (noon.getTime() > now.getTime() ? now : noon).toISOString();
}

/** Adresse de l'inscription d'une opération, préremplie depuis la bouteille, la machine ou l'intervention. */
export function operationNewPath(prefill: {
  cylinderId?: string; operation?: MachineOperation; equipmentId?: string; interventionId?: string;
}): string {
  const query = new URLSearchParams({
    ...(prefill.cylinderId ? { bouteille: prefill.cylinderId } : {}),
    ...(prefill.operation ? { type: prefill.operation } : {}),
    ...(prefill.equipmentId ? { machine: prefill.equipmentId } : {}),
    ...(prefill.interventionId ? { intervention: prefill.interventionId } : {}),
  }).toString();
  return `/fluides/operations/nouvelle${query ? `?${query}` : ''}`;
}

/** Bouteille et opération passées dans l'adresse, retenues seulement si elles sont valides. */
export function operationPrefill(params: URLSearchParams): { cylinderId: string | null; operation: MachineOperation | null } {
  const cylinder = params.get('bouteille');
  const operation = params.get('type');
  return {
    cylinderId: cylinder && UUID.test(cylinder) ? cylinder.toLowerCase() : null,
    operation: operation === 'charge' || operation === 'recuperation' ? operation : null,
  };
}

/** Adresse de la correction d'une opération du registre. */
export function correctionPath(id: string): string {
  return `/fluides/registre/${id}/correction`;
}

/** Ce qu'une ligne du registre dit d'elle pour la lire et la corriger. */
export interface RegistryMovement {
  kind: string;
  added_grams: number;
  recovered_grams: number;
  stock_grams: number;
  corrects_movement_id: string | null;
  corrected: boolean;
}

/** Quantité affichée : celle de l'opération, en négatif pour une contre-passation. */
export function movementGrams(movement: RegistryMovement): number {
  const grams = Math.abs(cylinderDeltaGrams(movement));
  return movement.corrects_movement_id ? -grams : grams;
}

/** Une opération se corrige une fois, sauf la contre-passation elle-même et le retour d'une bouteille. */
export function canCorrect(movement: RegistryMovement): boolean {
  return !movement.corrects_movement_id && !movement.corrected
    && ['charge', 'recuperation', 'reception'].includes(movement.kind) && movementGrams(movement) > 0;
}

/** Ce qui manque à une correction : clé de `fluides.correction`, ou `null`. */
export function correctionProblem(input: {
  choice: 'annuler' | 'quantite' | ''; kg: string; reason: string; originalGrams: number;
}): string | null {
  if (!input.choice) return 'manqueChoix';
  if (input.choice === 'quantite') {
    const grams = gramsFromKg(input.kg);
    if (!grams || grams <= 0) return 'manqueQuantite';
    if (grams === input.originalGrams) return 'memeQuantite';
  }
  if (input.reason.trim().length < FLUID_REASON_MIN) return 'manqueMotif';
  return null;
}

/** Devenir proposé du fluide d'une bouteille rendue, selon son usage et son état. */
export function defaultReturnNature(cylinder: { kind: string; status: string }): ReturnNature {
  if (cylinder.kind === 'charge') return 'reprise';
  return cylinder.status === 'pour_destruction' ? 'destruction' : 'regeneration';
}

export type ReturnNature = (typeof FLUID_NATURES.retour)[number];

/** Devenirs possibles du fluide d'une bouteille rendue : du fluide usagé n'est jamais « repris » comme du neuf. */
export function returnNatures(cylinder: { kind: string }): ReturnNature[] {
  return FLUID_NATURES.retour.filter((nature) => cylinder.kind === 'charge' || nature !== 'reprise');
}

// --- Onglets de la page Fluides -----------------------------------------------------------

export const FLUID_TABS = ['bouteilles', 'echeances', 'controles', 'cerfa', 'registre', 'bsff', 'detecteurs'] as const;
export type FluidTab = (typeof FLUID_TABS)[number];

/**
 * Onglet lu dans l'adresse (`onglet`). L'ancien nom `tab` (signets, liens
 * des versions précédentes) se lit encore ; une valeur inconnue ramène aux
 * bouteilles.
 */
export function resolveFluidTab(onglet: string | null, legacyTab?: string | null): FluidTab {
  const value = onglet || legacyTab || '';
  return FLUID_TABS.find((tab) => tab === value) ?? 'bouteilles';
}

/** Ancien nom du paramètre d'onglet, encore porté par des liens et des signets. */
const LEGACY_TAB = 'tab';

/** Onglet de l'adresse courante, ancien nom compris. */
export function fluidTabOf(params: URLSearchParams): FluidTab {
  return resolveFluidTab(params.get('onglet'), params.get(LEGACY_TAB));
}

/**
 * Adresse réécrite sous le nom commun quand elle porte encore l'ancien nom
 * (`?tab=cerfa` devient `?onglet=cerfa`), sinon `null`.
 */
export function legacyFluidsParams(params: URLSearchParams): URLSearchParams | null {
  if (!params.has(LEGACY_TAB)) return null;
  const tab = fluidTabOf(params);
  const next = new URLSearchParams(params);
  next.delete(LEGACY_TAB);
  if (tab === 'bouteilles') next.delete('onglet');
  else next.set('onglet', tab);
  return next;
}

/** Adresse d'un onglet de la page Fluides. */
export function fluidTabPath(tab: FluidTab): string {
  return tab === 'bouteilles' ? '/fluides' : `/fluides?onglet=${tab}`;
}

// --- Détecteurs de fuite --------------------------------------------------------------------

/** Étalonnage proche : les 30 jours avant l'échéance (le détecteur part chez l'étalonneur). */
const CALIBRATION_NOTICE_DAYS = 30;

export type DetectorStatus = 'retire' | 'expire' | 'bientot' | 'ok' | 'sans_echeance';

/** État d'un détecteur au jour donné (`AAAA-MM-JJ`) : retiré, échu, à renouveler bientôt, valide. */
export function detectorStatus(
  detector: { retired_at: string | null; calibration_due_on: string | null }, today: string,
): DetectorStatus {
  if (detector.retired_at) return 'retire';
  const due = detector.calibration_due_on;
  if (!due) return 'sans_echeance';
  if (due < today) return 'expire';
  const limit = new Date(`${today}T00:00:00Z`);
  limit.setUTCDate(limit.getUTCDate() + CALIBRATION_NOTICE_DAYS);
  return due <= limit.toISOString().slice(0, 10) ? 'bientot' : 'ok';
}

/** Adresse de la modification d'un détecteur (nouvel étalonnage compris). */
export function detectorEditPath(id: string): string {
  return `/fluides/detecteurs/${id}`;
}

export const NEW_DETECTOR_PATH = '/fluides/detecteurs/nouveau';

export type DetectorDraft = {
  serialNumber: string;
  model: string;
  lastCalibratedOn: string;
  calibrationDueOn: string;
  assignedUserId: string;
};

/** Ce qui empêche d'enregistrer le détecteur (clé de `fluides.detecteurs.manque…`), ou `null`. */
export function detectorDraftProblem(draft: DetectorDraft, initial: DetectorDraft): string | null {
  if (!draft.serialNumber.trim()) return 'manqueNumero';
  if (draft.lastCalibratedOn && draft.calibrationDueOn && draft.calibrationDueOn < draft.lastCalibratedOn) return 'echeanceAvant';
  if (JSON.stringify(draft) === JSON.stringify(initial)) return 'inchange';
  return null;
}

/** Page d'ajout d'un fluide absent de la liste MyEquip (décision 11). */
export const NEW_FLUID_PATH = '/fluides/types/nouveau';

// --- Échéances ----------------------------------------------------------------------------

export const DUE_PERIODS = ['retard', '30j', '90j', 'tous'] as const;
export type DuePeriod = (typeof DUE_PERIODS)[number];

export function resolveDuePeriod(value: string | null): DuePeriod {
  return DUE_PERIODS.find((period) => period === value) ?? '30j';
}

/** Urgence d'un prochain contrôle (date `AAAA-MM-JJ`) par rapport à aujourd'hui. */
export function dueTone(next: string | null, today: string): 'depasse' | 'bientot' | 'plus_tard' | 'aucune' {
  if (!next) return 'aucune';
  const day = next.slice(0, 10);
  if (day < today) return 'depasse';
  const soon = new Date(`${today}T00:00:00Z`);
  soon.setUTCDate(soon.getUTCDate() + 30);
  return day <= soon.toISOString().slice(0, 10) ? 'bientot' : 'plus_tard';
}

// --- Saisie d'une bouteille ---------------------------------------------------------------

/** Kilogrammes saisis (« 12,5 ») en grammes entiers, ou `null` si la saisie n'est pas un nombre. */
export function gramsFromKg(text: string): number | null {
  const kg = parseKg(text);
  return kg === null ? null : Math.round(kg * 1000);
}

export interface CylinderDraft {
  serialNumber: string;
  fluidTypeId: string;
  capacityKg: string;
  currentKg: string;
}

/** Ce qui manque à la saisie d'une bouteille, dans l'ordre des étapes (clé i18n), ou `null`. */
export function cylinderProblem(draft: CylinderDraft): string | null {
  if (!draft.serialNumber.trim()) return 'manqueNumero';
  if (!draft.fluidTypeId) return 'manqueFluide';
  const capacity = gramsFromKg(draft.capacityKg);
  if (!capacity || capacity <= 0) return 'manqueCapacite';
  const current = gramsFromKg(draft.currentKg);
  if (current === null) return 'manqueContenu';
  if (current > capacity) return 'tropPleine';
  return null;
}

// --- Préremplissage depuis l'adresse ------------------------------------------------------

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Intervention et machine passées dans l'adresse, retenues seulement si ce sont des identifiants. */
export function fluidPrefill(params: URLSearchParams): { interventionId: string | null; equipmentId: string | null } {
  const pick = (name: string): string | null => {
    const value = params.get(name);
    return value && UUID.test(value) ? value.toLowerCase() : null;
  };
  return { interventionId: pick('intervention'), equipmentId: pick('machine') };
}

/** Adresse de la préparation d'une fiche CERFA, préremplie depuis la fiche d'origine. */
export function cerfaNewPath(prefill: { interventionId?: string; equipmentId?: string }): string {
  const query = new URLSearchParams({
    ...(prefill.interventionId ? { intervention: prefill.interventionId } : {}),
    ...(prefill.equipmentId ? { machine: prefill.equipmentId } : {}),
  }).toString();
  return `/fluides/cerfa/nouvelle${query ? `?${query}` : ''}`;
}

/** Adresse de la saisie d'un contrôle d'étanchéité, préremplie depuis la fiche d'origine. */
export function leakCheckNewPath(prefill: { interventionId?: string; equipmentId?: string }): string {
  const query = new URLSearchParams({
    ...(prefill.equipmentId ? { machine: prefill.equipmentId } : {}),
    ...(prefill.interventionId ? { intervention: prefill.interventionId } : {}),
  }).toString();
  return `/fluides/controles/nouveau${query ? `?${query}` : ''}`;
}

// --- Fiches CERFA -------------------------------------------------------------------------

/** Titre d'une fiche : natures cochées (formulaire 15497*04) ou ancienne saisie libre. */
export function cerfaTitle(payload: Record<string, unknown>): string {
  const natures = CERFA_NATURES.filter((nature) => payload[nature.key] === true)
    .map((nature) => (nature.key === 'natureOther' && typeof payload.natureOtherText === 'string'
      ? payload.natureOtherText : nature.label));
  if (natures.length) return natures.join(', ');
  return typeof payload.operationNature === 'string' && payload.operationNature
    ? payload.operationNature
    : 'Fiche d’intervention';
}
